import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../App";
import api from "../services/api";
import "../styles/Checkout.css";
import { BASE_URL } from "../config";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
  "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir", "Ladakh",
];

const loadRazorpayScript = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;

    script.onload = () => {
      console.log('Razorpay script loaded successfully');
      resolve(true);
    };

    script.onerror = () => {
      console.error('Failed to load Razorpay script');
      reject(new Error('Failed to load Razorpay script'));
    };

    document.body.appendChild(script);
  });
};

export default function CheckoutPage() {
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, cartTotal, showToast, clearCart, user } = useApp();

  const { discountAmt = 0, couponCode = "" } = location.state || {};

  const [payment, setPayment] = useState("upi");
  const [step, setStep] = useState(1);
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({
    firstName: user?.name?.split(" ")[0] || "",
    lastName: user?.name?.split(" ").slice(1).join(" ") || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: "", city: "", state: "Tamil Nadu", pincode: "",
  });
  const [errors, setErrors] = useState({});

  // Load Razorpay script when component mounts
  useEffect(() => {
    loadRazorpayScript()
      .then(() => setRazorpayLoaded(true))
      .catch((err) => {
        console.error(err);
        showToast("Payment system unavailable. Please try COD.", "error");
      });
  }, []);

  const shipping = cartTotal >= 1000 ? 0 : 80;
  const codFee = payment === "cod" ? 40 : 0;
  const total = cartTotal - discountAmt + shipping + codFee;

  const validate = () => {
    const errs = {};
    if (!form.firstName) errs.firstName = "Required";
    if (!form.lastName) errs.lastName = "Required";
    if (!form.email || !form.email.includes("@")) errs.email = "Valid email required";
    if (!form.phone || form.phone.length < 10) errs.phone = "Valid 10-digit phone required";
    if (!form.address) errs.address = "Required";
    if (!form.city) errs.city = "Required";
    if (!form.pincode || form.pincode.length < 6) errs.pincode = "Valid 6-digit pincode required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: undefined }));
  };

  const buildOrderPayload = (razorpayPaymentId = null) => ({
    items: cart.map(i => ({
      productId: i.productId,
      name: i.name,
      brand: i.brand,
      image: i.image,
      price: i.price,
      qty: i.qty,
      size: i.size,
    })),
    shippingAddress: {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      address: form.address,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
    },
    paymentMethod: payment,
    couponCode,
    subtotal: cartTotal,
    shippingCost: shipping,
    codFee,
    discountAmount: discountAmt,
    totalAmount: total,
    ...(razorpayPaymentId && { razorpayPaymentId }),
  });

  // ── COD: place order directly ──
  const handleCOD = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      const order = await api.orders.place(buildOrderPayload());
      await clearCart();
      showToast(`Order ${order.orderId} placed successfully! 🎉`);
      navigate("/account");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to place order.", "error");
    } finally {
      setPlacing(false);
    }
  };

  // In your Checkout.jsx, update the handleUPI function:

  const handleUPI = async () => {
    if (!validate()) return;

    // Check if Razorpay is loaded
    if (!window.Razorpay) {
      console.log("Attempting to reload Razorpay...");
      try {
        await loadRazorpayScript();
      } catch (err) {
        showToast("Payment system not available. Please try COD or refresh the page.", "error");
        return;
      }
    }

    setPlacing(true);

    console.log("Starting UPI payment for amount:", total);

    try {
      // Step 1: Create Razorpay order on backend
      console.log("Calling createRazorpayOrder...");
      const response = await api.orders.createRazorpayOrder({ totalAmount: total });
      console.log("Razorpay order response:", response);

      const { razorpayOrderId, amount, currency, keyId } = response;

      // Step 2: Open Razorpay checkout
      const rzpOptions = {
        key: keyId,
        amount: amount,
        currency: currency,
        name: "NaturesDye",
        description: `Order – ${cart.length} item(s)`,
        order_id: razorpayOrderId,
        prefill: {
          name: `${form.firstName} ${form.lastName}`,
          email: form.email,
          contact: form.phone,
        },
        theme: { color: "#8B1A1A" },

        handler: async function (response) {
          console.log("Payment successful, verifying...", response);
          try {
            const verifyResponse = await api.orders.verifyPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });

            console.log("Payment verified, saving order...");

            const orderPayload = buildOrderPayload(verifyResponse.razorpayPaymentId);
            const order = await api.orders.place(orderPayload);

            await clearCart();
            showToast(`Order ${order.orderId} placed successfully! 🎉`);
            navigate("/account");
          } catch (err) {
            console.error("Order save error:", err);
            showToast(err.message || "Payment verified but order save failed.", "error");
          } finally {
            setPlacing(false);
          }
        },

        modal: {
          ondismiss: () => {
            console.log("Payment modal dismissed");
            setPlacing(false);
            showToast("Payment cancelled.", "error");
          },
        },
      };

      const rzp = new window.Razorpay(rzpOptions);

      rzp.on("payment.failed", (response) => {
        console.error("Payment failed:", response);
        setPlacing(false);
        showToast(response.error?.description || "Payment failed. Please try again.", "error");
      });

      rzp.open();

    } catch (err) {
      console.error("UPI payment error:", err);
      console.error("Error details:", err.message);
      showToast(err.message || "Could not initiate payment. Please try again.", "error");
      setPlacing(false);
    }
  };

  const handlePlaceOrder = () => {
    if (payment === "cod") return handleCOD();
    return handleUPI();
  };

  if (cart.length === 0) return (
    <div style={{ padding: "80px 0", textAlign: "center" }}>
      <div className="container">
        <div style={{ fontSize: 64, marginBottom: 16 }}>🛒</div>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 28, color: "var(--primary)", marginBottom: 12 }}>Your cart is empty!</h2>
        <p style={{ color: "var(--text-light)", marginBottom: 24 }}>Add items to cart before checking out.</p>
        <Link className="btn btn-primary" to="/collection">Shop Now</Link>
      </div>
    </div>
  );

  return (
    <div style={{ padding: "32px 0 80px" }}>
      <div className="container">
        <div className="collection-breadcrumb" style={{ marginBottom: 24 }}>
          <Link to="/">Home</Link><span>/</span>
          <Link to="/cart">Cart</Link><span>/</span>
          <span style={{ color: "var(--text-dark)", fontWeight: 600 }}>Checkout</span>
        </div>

        {/* Step Progress */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32 }}>
          {[["1", "Address"], ["2", "Payment"], ["3", "Review"]].map(([num, label], i) => (
            <div key={num} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: step > i ? "var(--primary)" : step === i + 1 ? "var(--gold)" : "var(--border)", color: step >= i + 1 ? "#fff" : "var(--text-light)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800 }}>
                {step > i + 1 ? "✓" : num}
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: step === i + 1 ? "var(--primary)" : "var(--text-light)" }}>{label}</span>
              {i < 2 && <div style={{ flex: 1, height: 2, background: step > i + 1 ? "var(--primary)" : "var(--border)", minWidth: 24 }} />}
            </div>
          ))}
        </div>

        <div className="checkout-layout">
          <div className="checkout-form">

            {/* Step 1: Address */}
            {step === 1 && (
              <>
                <div className="checkout-section-title">Shipping Address</div>
                <div className="form-row">
                  {[["firstName", "First Name", "Rajesh"], ["lastName", "Last Name", "Kumar"]].map(([k, l, ph]) => (
                    <div className="form-group" key={k}>
                      <label className="form-label">{l} *</label>
                      <input className="form-control" style={errors[k] ? { borderColor: "var(--primary)" } : {}} value={form[k]} onChange={e => handleChange(k, e.target.value)} placeholder={ph} />
                      {errors[k] && <p style={{ color: "var(--primary)", fontSize: 11, marginTop: 4 }}>{errors[k]}</p>}
                    </div>
                  ))}
                </div>
                <div className="form-row">
                  {[["email", "Email Address", "email", "you@email.com"], ["phone", "Phone Number", "tel", "9843961449"]].map(([k, l, t, ph]) => (
                    <div className="form-group" key={k}>
                      <label className="form-label">{l} *</label>
                      <input className="form-control" style={errors[k] ? { borderColor: "var(--primary)" } : {}} type={t} value={form[k]} onChange={e => handleChange(k, e.target.value)} placeholder={ph} maxLength={k === "phone" ? 10 : undefined} />
                      {errors[k] && <p style={{ color: "var(--primary)", fontSize: 11, marginTop: 4 }}>{errors[k]}</p>}
                    </div>
                  ))}
                </div>
                <div className="form-group">
                  <label className="form-label">Address *</label>
                  <textarea className="form-control" style={{ resize: "vertical", minHeight: 80, ...(errors.address ? { borderColor: "var(--primary)" } : {}) }} value={form.address} onChange={e => handleChange("address", e.target.value)} placeholder="House/Flat No, Street, Locality" />
                  {errors.address && <p style={{ color: "var(--primary)", fontSize: 11, marginTop: 4 }}>{errors.address}</p>}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">City *</label>
                    <input className="form-control" style={errors.city ? { borderColor: "var(--primary)" } : {}} value={form.city} onChange={e => handleChange("city", e.target.value)} placeholder="Chennai" />
                    {errors.city && <p style={{ color: "var(--primary)", fontSize: 11, marginTop: 4 }}>{errors.city}</p>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pincode *</label>
                    <input className="form-control" style={errors.pincode ? { borderColor: "var(--primary)" } : {}} type="number" value={form.pincode} onChange={e => handleChange("pincode", e.target.value)} placeholder="600001" maxLength={6} />
                    {errors.pincode && <p style={{ color: "var(--primary)", fontSize: 11, marginTop: 4 }}>{errors.pincode}</p>}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">State *</label>
                  <select className="form-control" value={form.state} onChange={e => handleChange("state", e.target.value)}>
                    {INDIAN_STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <button className="btn btn-primary btn-full" style={{ marginTop: 8, padding: 16 }} onClick={() => { if (validate()) setStep(2); }}>Continue to Payment →</button>
              </>
            )}

            {/* Step 2: Payment */}
            {step === 2 && (
              <>
                <div className="checkout-section-title">Payment Method</div>
                <div className="payment-options">
                  {[
                    { id: "upi", icon: "📱", label: "UPI (GPay, PhonePe, BHIM)" },
                    { id: "cod", icon: "💵", label: "Cash on Delivery (₹40 extra)" },
                  ].map(opt => (
                    <div key={opt.id} className={`payment-option ${payment === opt.id ? "selected" : ""}`} onClick={() => setPayment(opt.id)}>
                      <input type="radio" readOnly checked={payment === opt.id} id={`pay-${opt.id}`} />
                      <label htmlFor={`pay-${opt.id}`} className="payment-option-label">{opt.label}</label>
                      <span className="payment-option-icon">{opt.icon}</span>
                    </div>
                  ))}
                </div>

                {payment === "upi" && (
                  <div style={{ padding: 16, background: "var(--cream)", borderRadius: "var(--radius-md)", marginBottom: 16, fontSize: 13, color: "var(--text-mid)", lineHeight: 1.6 }}>
                    📱 You'll be redirected to Razorpay's secure payment page to complete your UPI payment via GPay, PhonePe, BHIM, or any UPI app.
                    {!razorpayLoaded && (
                      <div style={{ marginTop: 8, color: "#ff6b6b", fontSize: 12 }}>
                        ⚠️ Loading payment system... Please wait.
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: "flex", gap: 12 }}>
                  <button className="btn btn-outline" onClick={() => setStep(1)}>← Back</button>
                  <button className="btn btn-primary" style={{ flex: 1, padding: 16 }} onClick={() => setStep(3)}>Review Order →</button>
                </div>
              </>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
              <>
                <div className="checkout-section-title">Review Your Order</div>
                <div style={{ background: "var(--cream)", borderRadius: "var(--radius-md)", padding: 20, marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "var(--gold)", marginBottom: 8 }}>Shipping To</div>
                  <p style={{ fontWeight: 700, marginBottom: 4 }}>{form.firstName} {form.lastName}</p>
                  <p style={{ fontSize: 13, color: "var(--text-mid)", lineHeight: 1.7 }}>
                    {form.address}, {form.city}, {form.state} – {form.pincode}<br />
                    📞 {form.phone} &nbsp;|&nbsp; ✉ {form.email}
                  </p>
                  <a onClick={() => setStep(1)} style={{ fontSize: 12, fontWeight: 700, color: "var(--primary)", cursor: "pointer", textDecoration: "underline", marginTop: 8, display: "inline-block" }}>Edit</a>
                </div>

                <div style={{ background: "var(--cream)", borderRadius: "var(--radius-md)", padding: 20, marginBottom: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "var(--gold)", marginBottom: 8 }}>Payment Method</div>
                  <p style={{ fontWeight: 700 }}>{{ upi: "UPI (via Razorpay)", cod: "Cash on Delivery" }[payment]}</p>
                  <a onClick={() => setStep(2)} style={{ fontSize: 12, fontWeight: 700, color: "var(--primary)", cursor: "pointer", textDecoration: "underline", marginTop: 8, display: "inline-block" }}>Edit</a>
                </div>

                <p style={{ fontSize: 12, color: "var(--text-light)", marginBottom: 16, lineHeight: 1.6 }}>
                  By placing your order, you agree to our <a style={{ color: "var(--primary)", textDecoration: "underline" }}>Terms of Service</a> and <a style={{ color: "var(--primary)", textDecoration: "underline" }}>Privacy Policy</a>.
                </p>
                <div style={{ display: "flex", gap: 12 }}>
                  <button className="btn btn-outline" onClick={() => setStep(2)}>← Back</button>
                  <button className="btn btn-primary" style={{ flex: 1, padding: 16, fontSize: 14 }} onClick={handlePlaceOrder} disabled={placing}>
                    {placing
                      ? (payment === "upi" ? "Opening Payment…" : "Placing Order…")
                      : payment === "upi"
                        ? `💳 Pay ₹${total.toLocaleString()} via UPI`
                        : `🎉 Place Order – ₹${total.toLocaleString()}`}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Order Summary Aside */}
          <div className="order-summary-aside">
            <h3>Your Order</h3>
            {cart.map(item => (
              <div className="order-item" key={item.key}>
                <div className="order-item-img">
                  {/* <img src={item.image} alt={item.name} onError={e => { e.target.src = `https://placehold.co/60x74/f0e8dc/8B1A1A?text=IMG`; }} /> */}
                  <img src={`${BASE_URL}${item.image}`} alt={item.name} onError={e => { e.target.src = `https://placehold.co/90x110/f0e8dc/8B1A1A?text=IMG`; }} />
                  <span className="order-item-qty">{item.qty}</span>
                </div>
                <div className="order-item-info">
                  <div className="order-item-name">{item.name}</div>
                  <div className="order-item-variant">Size: {item.size}</div>
                </div>
                <div className="order-item-price">₹{(item.price * item.qty).toLocaleString()}</div>
              </div>
            ))}
            <div className="order-divider" />
            <div className="summary-row"><span>Subtotal</span><span>₹{cartTotal.toLocaleString()}</span></div>
            {discountAmt > 0 && <div className="summary-row" style={{ color: "#2d7a2d" }}><span>Discount</span><span>−₹{discountAmt}</span></div>}
            <div className="summary-row"><span>Shipping</span><span>{shipping === 0 ? <span style={{ color: "#2d7a2d", fontWeight: 700 }}>FREE</span> : `₹${shipping}`}</span></div>
            {payment === "cod" && <div className="summary-row"><span>COD Fee</span><span>₹40</span></div>}
            <div className="order-divider" />
            <div className="summary-row total"><span>Total</span><span>₹{total.toLocaleString()}</span></div>
            <div style={{ marginTop: 16, padding: "12px", background: "var(--cream)", borderRadius: "var(--radius)", fontSize: 12, color: "var(--text-mid)", lineHeight: 1.6 }}>
              🔒 Payments secured by Razorpay with 256-bit SSL encryption.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}